import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Pause, Play, Pencil, ArrowRight, LocateFixed, Loader2, CalendarCheck, PlayCircle, Gauge, DollarSign, Building2, Megaphone, BarChart3, Search, RefreshCw, MessageSquare } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StatusBadge from "../components/StatusBadge";
import { PageLoading, EmptyState, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { fmtMoney, fmtDateTimeZone, setPageTitle } from "../lib/format";
import { fadeUp, revealOnce, staggerParent } from "../lib/motion";
import { toast } from "sonner";

const CONNECT_STATES = {
  not_connected: ["Not connected", "text-zinc-400"],
  setup_required: ["Setup required", "text-amber-400"],
  verification_pending: ["Verification pending", "text-blue-400"],
  action_required: ["Action required", "text-amber-400"],
  restricted: ["Restricted", "text-red-400"],
  payouts_disabled: ["Payouts disabled", "text-red-400"],
  ready: ["Ready", "text-emerald-400"],
};

export default function HostDashboard() {
  const [dash, setDash] = useState(null);
  const [spots, setSpots] = useState(null);
  const [connect, setConnect] = useState(null);
  const [current, setCurrent] = useState(null); // live bookings (Part 13)
  const [error, setError] = useState(null);
  const [locatingId, setLocatingId] = useState(null);
  const [overview, setOverview] = useState(null); // server-side aggregates (V2 correction pass)
  const [listQuery, setListQuery] = useState("");

  // One-shot proximity verification (§6): browser geolocation is sent once,
  // compared server-side, and discarded — only a verified flag/band is stored.
  const verifyLocation = (s) => {
    if (!navigator.geolocation) { toast.error("Geolocation is not supported by this browser"); return; }
    setLocatingId(s.spot_id);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.verifySpotLocation(s.spot_id, { latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          toast.success("Parking location verified");
          setSpots(await api.mySpots());
        } catch (e) { toast.error(e.message); }
        finally { setLocatingId(null); }
      },
      (err) => {
        setLocatingId(null);
        toast.error(err.code === 1 ? "Location permission denied" : "Location unavailable — try again");
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  const load = () => {
    setError(null);
    api.hostDashboard().then(setDash).catch((e) => setError(e.message));
    api.mySpots().then(setSpots).catch(() => setSpots([]));
    api.connectStatus().then(setConnect).catch(() => setConnect(null));
    api.hostOverview().then(setOverview).catch(() => setOverview(null));
    // CURRENT BOOKINGS (Part 13): authorized/paid reservations whose window has
    // not ended — straight from the existing host bookings API, nothing duplicated.
    api.hostBookings().then((bs) => setCurrent(
      bs.filter((b) => ["authorized", "paid"].includes(b.status) && new Date(b.end_time) > new Date())
    )).catch(() => setCurrent([]));
  };
  useEffect(() => { setPageTitle("Host Dashboard"); load(); }, []);

  const togglePause = async (s) => {
    try {
      await api.updateSpot(s.spot_id, { paused: !s.paused });
      toast.success(s.paused ? "Listing resumed" : "Listing paused");
      setSpots(await api.mySpots());
    } catch (e) { toast.error(e.message); }
  };

  const chartData = dash ? dash.chart.months.map((m, i) => ({ month: m, earnings: dash.chart.values[i] })) : [];
  const [csLabel, csCls] = CONNECT_STATES[connect?.state] || CONNECT_STATES.not_connected;

  return (
    <div className="min-h-screen flex flex-col" data-testid="host-dashboard">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <motion.div variants={staggerParent(0.06)} initial="hidden" animate="show" className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <motion.p variants={fadeUp} className="section-label">Host</motion.p>
            <motion.h1 variants={fadeUp} className="type-display mt-3">Your parking business.</motion.h1>
          </div>
          <motion.div variants={fadeUp}>
            <Link to="/host/listings/new" data-testid="create-listing-cta" className="btn-primary text-sm"><Plus className="w-4 h-4" /> New Listing</Link>
          </motion.div>
        </motion.div>

        {error ? <div className="mt-10"><ErrorState body={error} onRetry={load} /></div> : !dash ? <div className="mt-10"><PageLoading label="Loading host dashboard…" /></div> : (
          <>
            <motion.div variants={staggerParent(0.07)} {...revealOnce} className="mt-10 grid grid-cols-2 lg:grid-cols-4" data-testid="host-stats">
              {[
                { label: "Total earnings", value: fmtMoney(dash.earnings_total), tid: "host-stat-earnings", to: "/payouts" },
                { label: "This month", value: fmtMoney(dash.earnings_month), tid: "host-stat-month", to: "/payouts" },
                { label: "Total bookings", value: dash.total_bookings, tid: "host-stat-bookings", to: "/host/bookings" },
                { label: "Active listings", value: `${dash.active_spots}/${dash.total_spots}`, tid: "host-stat-spots", to: null },
              ].map((s, i) => (
                <motion.div key={s.label} variants={fadeUp} className={`${i > 0 ? "sm:pl-6 sm:border-l border-[#1F2328]" : ""} ${i >= 2 ? "border-t sm:border-t-0 border-[#1F2328] max-sm:pt-6" : ""}`}>
                  {s.to ? (
                    <Link to={s.to} data-testid={s.tid}
                          className="block py-6 pr-6 rounded-lg group transition-colors hover:bg-white/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E50914]">
                      <p className="font-display font-bold text-4xl tracking-tight group-hover:text-[#FF3B45] transition-colors">{s.value}</p>
                      <p className="meta-label mt-1.5">{s.label}</p>
                    </Link>
                  ) : (
                    <div className="py-6 pr-6" data-testid={s.tid}>
                      <p className="font-display font-bold text-4xl tracking-tight">{s.value}</p>
                      <p className="meta-label mt-1.5">{s.label}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>

            {/* Drill-down command grid — every card leads to a real detail screen */}
            {overview && (
              <motion.div variants={staggerParent(0.05)} {...revealOnce} className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="host-overview-grid">
                {[
                  { label: "Today's bookings", value: overview.bookings_today, to: "/host/bookings", tid: "ov-today", Icon: CalendarCheck },
                  { label: "Active sessions", value: overview.active_sessions, to: "/host/bookings", tid: "ov-active", Icon: PlayCircle },
                  { label: "Occupancy", value: `${overview.occupancy_pct}%`, to: "/host/bookings", tid: "ov-occupancy", Icon: Gauge },
                  { label: `Revenue · ${overview.month}`, value: fmtMoney(overview.revenue_month), to: "/payouts", tid: "ov-revenue", Icon: DollarSign },
                  { label: "Pending payouts", value: fmtMoney(dash.pending_balance), to: "/payouts", tid: "ov-pending", Icon: DollarSign },
                  { label: "Commercial properties", value: overview.properties_count, to: "/commercial", tid: "ov-properties", Icon: Building2 },
                  { label: "Active drivers & chat", value: current?.length ?? 0, to: "/host/bookings", tid: "ov-drivers", Icon: MessageSquare },
                  { label: "Announcements", value: "Manage", to: "/commercial", tid: "ov-announcements", Icon: Megaphone },
                  { label: "Earnings & invoices", value: "View", to: "/host/invoices", tid: "ov-earnings", Icon: BarChart3 },
                ].map((c) => (
                  <Link key={c.tid} to={c.to} data-testid={c.tid}
                        className="card-dark card-dark-hover p-5 group focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E50914]">
                    <c.Icon className="w-4 h-4 text-[#FF3B45]" />
                    <p className="mt-3 font-display font-bold text-2xl tracking-tight group-hover:text-[#FF3B45] transition-colors">{c.value}</p>
                    <p className="meta-label mt-1">{c.label}</p>
                  </Link>
                ))}
              </motion.div>
            )}

            <div className="mt-14 grid lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2" data-testid="earnings-chart-card">
                <div className="flex items-center justify-between">
                  <h2 className="type-h2">Earnings</h2>
                  <p className="meta-label">Last 6 months</p>
                </div>
                <div className="h-64 mt-6 -ml-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ left: 0, right: 8, top: 8 }}>
                      <defs>
                        <linearGradient id="eg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#E50914" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#E50914" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="month" stroke="#3F3F46" fontSize={11} tickLine={false} axisLine={false} fontFamily="JetBrains Mono" />
                      <YAxis stroke="#3F3F46" fontSize={11} tickLine={false} axisLine={false} fontFamily="JetBrains Mono" />
                      <Tooltip contentStyle={{ background: "#0D1114", border: "1px solid #27272A", borderRadius: 10, fontSize: 12 }} formatter={(v) => [fmtMoney(v), "Earnings"]} />
                      <Area type="monotone" dataKey="earnings" stroke="#E50914" strokeWidth={2} fill="url(#eg)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <Link to="/wallet" className="btn-ghost-red text-xs" data-testid="open-wallet-link">Wallet: {fmtMoney(dash.available_balance)} <ArrowRight className="w-3.5 h-3.5" /></Link>
              </div>
              <div className="card-dark p-7" data-testid="payout-status-card">
                <p className="meta-label">Payout status</p>
                <p className={`mt-3 font-display font-semibold text-xl ${csCls}`} data-testid="connect-state">{csLabel}</p>
                <p className="mt-2 text-xs text-zinc-500 leading-relaxed">Payouts run on Stripe Connect. Drivers pay your listed price plus the MyRedPark service fee — you receive your listed price minus the applicable MyRedPark host service fee.</p>
                <div className="mt-5 space-y-2.5 text-sm border-t border-[#1F2328] pt-5">
                  <div className="flex justify-between text-zinc-400"><span>Available balance</span><span className="text-white font-semibold font-display">{fmtMoney(dash.available_balance)}</span></div>
                  <div className="flex justify-between text-zinc-400"><span>Processing</span><span className="text-white font-semibold font-display">{fmtMoney(dash.pending_balance)}</span></div>
                </div>
                <Link to="/payouts" data-testid="manage-payouts-link" className="btn-primary w-full mt-6 text-sm">Manage Payouts</Link>
                <Link to="/host/bookings" data-testid="host-bookings-link" className="btn-secondary w-full mt-2.5 text-sm">All Host Bookings <ArrowRight className="w-3.5 h-3.5" /></Link>
              </div>
            </div>
          </>
        )}

        {current && current.length > 0 && (
          <div className="mt-14 card-dark p-6 border-[#E50914]/25" data-testid="host-current-bookings">
            <div className="flex items-center justify-between mb-4">
              <h2 className="type-h2">Current bookings</h2>
              <span className="text-xs font-bold text-[#FF3B45]" data-testid="host-current-count">{current.length} active</span>
            </div>
            <div className="divide-y divide-[#18181B]">
              {current.slice(0, 3).map((b) => (
                <Link key={b.booking_id} to={`/bookings/${b.booking_id}`} data-testid={`host-current-booking-${b.booking_id}`}
                      className="flex items-center justify-between gap-4 py-3.5 group transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate group-hover:text-white transition-colors">{b.spot_title}</p>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      {b.driver_name || "Driver"} · {fmtDateTimeZone(b.start_time, b.timezone)} <ArrowRight className="inline w-3 h-3 -mt-0.5" aria-hidden="true" /> {fmtDateTimeZone(b.end_time, b.timezone)}
                    </p>
                  </div>
                  <StatusBadge value={b.status} />
                </Link>
              ))}
            </div>
            <Link to="/host/bookings" data-testid="host-view-active-bookings" className="btn-ghost-red text-xs mt-3 inline-flex">View active bookings <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        )}

        <div className="mt-16">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="type-h2">Your listings</h2>
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
                <input value={listQuery} onChange={(e) => setListQuery(e.target.value)} placeholder="Search listings…" aria-label="Search listings"
                       className="rounded-xl border border-[#27272A] bg-[#0A0D10] pl-9 pr-3 py-2 text-xs text-white w-48 focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors" data-testid="host-listings-search" />
              </div>
              <button onClick={load} className="btn-secondary !py-2 !px-3 text-xs" data-testid="host-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
            </div>
          </div>
          {spots === null ? <PageLoading label="Loading listings…" /> : spots.length === 0 ? (
            <EmptyState title="No listings yet" body="Create your first listing and start earning from your space."
              action={<Link to="/host/listings/new" data-testid="empty-create-listing" className="btn-primary text-sm"><Plus className="w-4 h-4" /> Create Listing</Link>} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="host-listings">
              {spots.filter((s) => { const q = listQuery.trim().toLowerCase(); return !q || (s.title || "").toLowerCase().includes(q) || (s.address || "").toLowerCase().includes(q) || (s.property_code || "").includes(q); }).map((s) => (
                <div key={s.spot_id} className="card-dark card-dark-hover p-6" data-testid={`host-listing-${s.spot_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/spot/${s.spot_id}`} className="font-display font-semibold hover:text-[#FF3B45] transition-colors line-clamp-1">{s.title}</Link>
                      <p className="text-xs text-zinc-600 mt-1.5 line-clamp-1">{s.address}</p>
                    </div>
                    {(!s.status || s.status === "active") ? (
                      <StatusBadge value={s.paused ? "occupied" : "available"} />
                    ) : (
                      <span data-testid={`listing-status-${s.spot_id}`}
                            className={`text-[0.62rem] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border whitespace-nowrap ${s.status === "pending_review" || s.status === "under_review" ? "border-amber-500/40 text-amber-400" : "border-red-500/40 text-red-400"}`}>
                        {s.status === "pending_review" ? "Pending review" : s.status === "under_review" ? "Under review" : s.status === "rejected" ? "Not approved" : "Suspended"}
                      </span>
                    )}
                  </div>
                  {s.verification?.proximity_verified_at && <p className="mt-2 text-[0.65rem] font-semibold text-emerald-400" data-testid={`listing-verified-${s.spot_id}`}>Location verified</p>}
                  {s.review_reason && s.status !== "active" && <p className="mt-2 text-xs text-amber-400/90" data-testid={`listing-review-reason-${s.spot_id}`}>{s.review_reason}</p>}
                  {s.property_code && (
                    <p className="mt-2 text-xs text-zinc-500" data-testid={`listing-code-${s.spot_id}`}>
                      Property code <span className="font-mono font-bold tracking-[0.18em] text-[#FF3B45]">{s.property_code}</span>
                      <span className="text-zinc-600"> · QR &amp; sign on the edit page</span>
                    </p>
                  )}
                  <p className="mt-4 font-display font-bold text-xl">{fmtMoney(s.price_per_hour)}<span className="text-xs text-zinc-600 font-body font-normal">/hr</span></p>
                  <div className="mt-5 flex gap-2">
                    <Link to={`/host/listings/${s.spot_id}/edit`} data-testid={`edit-listing-${s.spot_id}`} className="btn-secondary flex-1 !px-3 !py-2 text-xs"><Pencil className="w-3.5 h-3.5" /> Edit</Link>
                    <button onClick={() => togglePause(s)} data-testid={`pause-listing-${s.spot_id}`} className="btn-secondary flex-1 !px-3 !py-2 text-xs">
                      {s.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />} {s.paused ? "Resume" : "Pause"}
                    </button>
                    {!s.verification?.proximity_verified_at && (
                      <button onClick={() => verifyLocation(s)} disabled={locatingId === s.spot_id} data-testid={`verify-location-${s.spot_id}`}
                              className="btn-secondary flex-1 !px-3 !py-2 text-xs" title="Verify you are physically at the parking location">
                        {locatingId === s.spot_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5" />} Verify
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
